import { classNames } from 'primereact/utils';
import { Checkbox } from 'primereact/checkbox';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Goal, GoalStep } from 'types/Goal';
import { ProgressBar } from 'components/_new/ProgressBar';

import { Box, BoxSection } from 'components/_new/Box';
import getGoalRows from './components/getGoalRows';

interface Props {
	goalData: Goal;
	bold?: boolean;
	onGoalStepToggle: (stepId: number) => void;
}

const GoalText = ({ goalData, bold = false, onGoalStepToggle }: Props) => {
	const { t } = useTranslation();
	const rows = getGoalRows(goalData, t).filter((row) => {
		if (row.type === 'goal') return false; // goal is in the header
		if (row.isExtended) {
			if (row.arr) return row.arr.length > 0;
			return row.text && row.text.trim().length > 0;
		}
		return true;
	});

	return (
		<div>
			{rows?.map(({ label, type, text, arr }, index) => (
				<div key={label} className="grid mt-2">
					<div
						className={classNames('col-12 lg:col-4 p-2', {
							'font-bold': bold,
						})}
					>
						{label}
					</div>

					<div className="col-12 lg:col-8 pb-4 lg:pb-2">
						{!arr && (
							<div className="font-bold" style={{ whiteSpace: 'pre-line' }}>
								{text || '-'}
							</div>
						)}

						{type === 'actions' && arr && (
							<>
								{arr.length === 0 && <div>-</div>}
								{arr.length > 0 && (
									<Box variant="darkblue">
										<BoxSection>
											<ol className="m-0">
												{arr.map((item) => (
													<li key={item.id} style={{ whiteSpace: 'pre-line' }}>
														{item.text || '-'}
													</li>
												))}
											</ol>
										</BoxSection>
									</Box>
								)}
							</>
						)}

						{type === 'steps' && arr && (
							<>
								{arr.length === 0 && <div>-</div>}
								{arr.length > 0 && (
									<Box variant="darkblue">
										<BoxSection>
											{arr.length > 2 && (
												<div className="grid align-items-center mb-2">
													<div className="col-12 lg:col-3 md:col-4">
														<span className="text-gray text-nowrap mr-2">
															{t('userPanel.goal.progressInRealization')}
														</span>
													</div>
													<div className="col-12 lg:col-9 md:col-8">
														<ProgressBar
															current={
																arr.filter(
																	(item) => 'isFulfilled' in item && item.isFulfilled
																).length
															}
															max={arr.length}
														/>
													</div>
												</div>
											)}
											<div className="m-0 flex flex-column gap-1">
												{(arr as GoalStep[]).map((item, index) => (
													<div key={item.id} style={{ whiteSpace: 'pre-line' }}>
														<div className="flex align-items-start gap-2">
															<Checkbox
																inputId={`goal-step-${item.id}`}
																checked={item.isFulfilled}
																tooltip={
																	item.isFulfilled
																		? t('userPanel.goal.markAsUncompleted')
																		: t('userPanel.goal.markAsCompleted')
																}
																tooltipOptions={{ position: 'top' }}
																onChange={() => onGoalStepToggle(item.id)}
															/>
															<span>{index + 1}.</span>
															<label htmlFor={`goal-step-${item.id}`}>
																{item.text || '-'}
															</label>
														</div>
													</div>
												))}
											</div>
										</BoxSection>
									</Box>
								)}
							</>
						)}
					</div>
				</div>
			))}
		</div>
	);
};

export default GoalText;
